<template>
    <tenants-current v-slot="{ data: tenant }">
        <applications-find
            v-slot="{
                data: application,
                error,
                is_loading,
                has_loaded,
                find
            }"
            :params="{
                application: $route.params.application
            }"
            :include="[
                'applicant(include:tenant,address,employers,landlord)',
                'coapplicants(include:tenant,address,employers,landlord)',
                'listing(include:property(include:address))',
                'note',
            ]"
        >
            <div>
                <template v-if="!has_loaded || error">
                    <base-loading
                        :error="error"
                        :retry="find"
                        class="py-6"
                    />

                    <error-modal
                        v-if="error"
                        :error="error"
                    />
                </template>

                <template v-else>
                    <with-variables
                        v-slot="{ applicant }"
                        :variables="{
                            applicant: _.find([
                                application.applicant,
                                ...application.coapplicants.data
                            ], applicant => {
                                return tenant && _.get(applicant, 'tenant.uuid') === tenant.uuid
                            })
                        }"
                    >
                        <div>
                            <details-card class="mb-6">
                                <template slot="header">
                                    <div class="flex-grow mr-3 overflow-hidden">
                                        <h1 class="text-lg font-normal truncate">
                                            Application for {{ application.listing.property.address.name }}
                                        </h1>
                                    </div>

                                    <div
                                        v-if="is_loading"
                                        class="w-6 h-6 mr-3"
                                    >
                                        <base-svg
                                            name="icon-loading"
                                            class="w-full h-full"
                                        />
                                    </div>
                                </template>
                                <template slot="body">
                                    <grid-layout>
                                        <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                            <div class="text-xs text-label">Listing</div>
                                            <div class="text-sm">
                                                <router-link
                                                    v-if="$gate.role('landlord')"
                                                    :to="{
                                                        name: 'dashboard.landlords.listings.show',
                                                        params: {
                                                            listing: application.listing.uuid
                                                        }
                                                    }"
                                                    class="text-blue"
                                                >
                                                    {{ application.listing.property.name }}
                                                </router-link>

                                                <router-link
                                                    v-else
                                                    :to="{
                                                        name: 'listings.show',
                                                        params: {
                                                            listing: application.listing.uuid
                                                        }
                                                    }"
                                                    class="text-blue"
                                                >
                                                    {{ application.listing.property.address.name }}
                                                </router-link>
                                            </div>
                                        </grid-layout-item>

                                        <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                            <div class="text-xs text-label">Applied at</div>
                                            <div class="text-sm">
                                                {{ application.submitted_at | moment('DD/MM/YYYY') }}
                                            </div>
                                        </grid-layout-item>
                                    </grid-layout>

                                    <template v-if="applicant">
                                        <hr class="mb-3">

                                        <grid-layout>
                                            <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                                <div class="text-xs text-label">Email</div>
                                                <div class="text-sm">
                                                    <a-mailto
                                                        v-if="applicant.email"
                                                        :email="applicant.email"
                                                        class="text-blue"
                                                    />
                                                    <base-na v-else />
                                                </div>
                                            </grid-layout-item>

                                            <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                                <div class="text-xs text-label">Telephone</div>
                                                <div class="text-sm">
                                                    <a-tel
                                                        v-if="applicant.telephone"
                                                        :telephone="applicant.telephone"
                                                        class="text-blue"
                                                    />
                                                    <base-na v-else />
                                                </div>
                                            </grid-layout-item>

                                            <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                                <div class="text-xs text-label">Date of birth</div>
                                                <div class="text-sm">
                                                    {{ applicant.date_of_birth | moment('DD/MM/YYYY') }}
                                                </div>
                                            </grid-layout-item>

                                            <grid-layout-item>
                                                <div class="text-xs text-label">Address</div>
                                                <div class="text-sm">
                                                    <base-address
                                                        :is-inline="true"
                                                        :address="applicant.address"
                                                    />
                                                </div>
                                            </grid-layout-item>
                                        </grid-layout>
                                    </template>
                                </template>
                            </details-card>

                            <template v-if="applicant">
                                <div class="mb-6 bg-white rounded shadow">
                                    <header class="flex items-center h-16 px-4 bg-white border-b rounded-t sm:px-6">
                                        <h3 class="flex-grow text-base font-normal">Employers</h3>
                                    </header>

                                    <applicant-employers-list
                                        v-if="applicant.employers.data.length"
                                        :employers="applicant.employers.data"
                                    />

                                    <div
                                        v-else
                                        class="p-3 italic text-center text-grey bg-grey-bg"
                                    >
                                        There's nothing here!
                                    </div>
                                </div>

                                <div class="mb-6 rounded shadow bg-grey-bg">
                                    <header class="flex items-center h-16 px-4 bg-white border-b rounded-t sm:px-6">
                                        <h3 class="flex-grow text-base font-normal">Landlord</h3>
                                    </header>

                                    <div
                                        v-if="applicant.landlord && applicant.landlord.first_name"
                                        class="px-4 pb-3 sm:px-6 md:pb-4"
                                    >
                                        <grid-layout>
                                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                                <div class="text-xs text-label">Name</div>
                                                <div class="text-sm">
                                                    {{ applicant.landlord.first_name }} {{ applicant.landlord.last_name }}
                                                </div>
                                            </grid-layout-item>

                                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                                <div class="text-xs text-label">Email</div>
                                                <div class="text-sm">
                                                    <a-mailto
                                                        v-if="applicant.landlord.email"
                                                        :email="applicant.landlord.email"
                                                        class="text-blue"
                                                    />
                                                    <base-na v-else />
                                                </div>
                                            </grid-layout-item>

                                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                                <div class="text-xs text-label">Telephone</div>
                                                <div class="text-sm">
                                                    <a-tel
                                                        v-if="applicant.landlord.telephone"
                                                        :telephone="applicant.landlord.telephone"
                                                        class="text-blue"
                                                    />
                                                    <base-na v-else />
                                                </div>
                                            </grid-layout-item>
                                        </grid-layout>
                                    </div>

                                    <div
                                        v-else
                                        class="p-3 italic text-center text-grey bg-grey-bg"
                                    >
                                        There's nothing here!
                                    </div>
                                </div>
                            </template>

                            <div class="mb-6 bg-white rounded shadow">
                                <header class="flex items-center h-16 px-4 bg-white border-b rounded-t sm:px-6">
                                    <h3 class="flex-grow text-base font-normal">
                                        {{ $gate.role('landlord') ? 'Applicants' : 'Co-Applicants' }}
                                    </h3>

                                    <div
                                        v-if="$gate.role('landlord')"
                                        class="flex-none"
                                    >
                                        <chats-store-button
                                            :personas="_.chain([
                                                application.applicant,
                                                ...application.coapplicants.data
                                            ]).map('tenant').filter().value()"
                                        >
                                            Message Applicants
                                        </chats-store-button>
                                    </div>
                                </header>

                                <applicants-list
                                    :applicants="_.filter([
                                        application.applicant,
                                        ...application.coapplicants.data
                                    ], applicant => {
                                        return !tenant || _.get(applicant, 'tenant.uuid') !== tenant.uuid
                                    })"
                                />
                            </div>
                        </div>
                    </with-variables>
                </template>
            </div>
        </applications-find>
    </tenants-current>
</template>

<script>
    import ApplicantEmployersList from '../../../../components/applicant-employers-list'
    import ApplicantsList from '../../../../components/applicants-list'
    import ApplicationsFind from '../../../../components/applications-find'
    import ChatsStoreButton from '../../../../components/chats-store-button'
    import DetailsCard from '../../../../components/details-card'
    import TenantsCurrent from '../../../../components/tenants-current'

    export default {
        components: {
            ApplicantEmployersList,
            ApplicantsList,
            ApplicationsFind,
            ChatsStoreButton,
            DetailsCard,
            TenantsCurrent
        }
    }
</script>
